<template>
    <main class="blog">
        <page-hero title="Blog" subtitle="News, updates, and more!" />
        <div class="blog-post-wrapper">
            <article class="box">
                <section class="blog-post-items columns is-multiline">
                    <div class="blog-post" v-for="post in posts" :key="post.sys.id">
                        <div class="article-content">
                            <p class="has-text-gray blog-post-info">
                                <img loading="lazy" class="author-image" :src="setSize(post.fields.authorImage, 100)"/>
                                &nbsp;{{ post.fields.author }} on {{ post.fields.date.toLocaleDateString() }}<span v-if="post.fields.length"> | {{ post.fields.length }} min read</span>
                            </p>
                            <h2 class="title is-4 clickable" @click="() => route(post)" aria-role="button">{{ post.fields.title }}</h2>
                            <p class="blog-preview clickable" @click="() => route(post)" aria-role="button">{{ post.preview }}</p>
                            <router-link :to="postPath(post)" class="link has-text-info">Read more</router-link>
                        </div>
                        <img loading="lazy" class="blog-image clickable" @click="() => route(post)" aria-role="button" :src="setSize(post.fields.image, 600)"/>
                    </div>
                </section>
                <div class="end-of-blog">
                    <p class="has-text-centered has-text-gray">Sorry, you've reached the end of the blog. Thanks for reading!</p>
                </div>
            </article>
            <aside class="blog-sidebar box" v-if="sidebarPosts.length">
                <h3 class="sidebar-heading">More posts</h3>
                <ul>
                    <li v-for="post in sidebarPosts" :key="post.sys.id">
                        <router-link :to="postPath(post)" class="has-text-dark">{{ post.fields.title }}</router-link>
                    </li>
                </ul>
            </aside>
        </div>
    </main>
</template>

<style scoped lang="scss">

.clickable {
    cursor: pointer;
}

@media screen and (max-width: 768px) {
    .blog-post .blog-image {
        display: none;
    }
}

.blog {
    background-color: #eee;
    min-height: 700px;
    padding-bottom: 100px;

    .blog-post-wrapper {
        display: flex;
        align-items: flex-start;
        gap: 1.5rem;
        position: relative;
        max-width: $widescreen;
        margin: 0 auto;
        padding: 1.5rem;

        .box {
            flex: 1;
            min-width: 0;
        }

        .blog-sidebar {
            flex: 0 0 280px;
            text-align: left;
            padding: 1.75rem;

            .sidebar-heading {
                margin: 0 0 0.5rem 0;
                font-size: 0.8rem;
                font-weight: 700;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                color: $primary-dark;
            }

            ul {
                list-style: none;
                margin: 0;
                padding: 0;
            }

            li {
                padding: 1rem 0;
                border-bottom: 1px solid $gray-light;

                &:last-child {
                    border-bottom: none;
                }
            }

            a {
                display: block;
                text-align: left;
                font-size: 1.05rem;
                font-weight: 600;
                line-height: 1.4;

                &:hover {
                    color: $primary-dark;
                }
            }
        }

        // Side-by-side is a desktop-and-up layout. Below that (tablet and
        // mobile — @include touch covers both) the sidebar drops beneath the
        // main feed rather than squeezing into a column too narrow for a
        // headline to read comfortably.
        @include touch {
            flex-direction: column;

            .blog-sidebar {
                flex-basis: auto;
                width: 100%;
            }
        }

        .blog-post-items {
            display: flex;
            background-color: white;

        
            .blog-post {
                display: flex;
                padding: 2rem;
                text-align: left;
                font-size: 1.25rem;

                align-items: center;

                .author-image {
                    width: 35px;
                    height: 35px;
                    object-fit: cover;
                    object-position: center;
                    border-radius: 50%;
                    transform: translateY(25%);
                    margin-right: 0.5rem;
                }

                .blog-post-info {
                    line-height: 50px;
                    vertical-align: middle;
                }
                
                .blog-image {
                    width: 300px;
                    height: auto;
                    object-fit: cover;
                    object-position: left;
                    border-radius: 0.5rem;
                }

                .title {
                    text-align: left;
                    margin-top: 1rem;
                    color: black;
                    letter-spacing: -0.05rem;

                }

                img {
                    object-fit: cover;
                    width: 300px;
                    border-radius: 0.5rem
                }

                .blog-preview {
                    margin-top: 1rem;
                    color: #666;
                    margin-bottom: 1rem;
                    padding-right: 1rem;
                }

                &:not(:last-child) {
                    border-bottom: 0.5px solid $gray-light;
                }
            }

            min-height: 450px;
        }
    }

}

</style>

<script>
import PageHero from '../components/Page/PageHero.vue'
import { getEntries, pickRotating, postPath } from '../util/contentful'

// A post with no hero still deserves a card. Main added this when missing
// Contentful fields were taking whole posts down; the fetching moved into
// util/contentful.js since, but the fallback belongs here, next to the
// component that renders it.
const FALLBACK_IMAGE = '/images/social-graphic.png'


export default {
    name: 'Blog',
    components: {
        PageHero
    },

    data() {
        return {
            allPosts: [],
            posts: [],
            includes: {},
            // Picked once per page load rather than kept live in sync with
            // `posts` — this is a "more to read" rail, not a data view, and
            // reshuffling it while someone's reading would be more
            // distracting than useful.
            sidebarPosts: [],
        }
    },
    mounted() {
        this.getPosts()

        let observer = new IntersectionObserver(function(entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.updatePosts.bind(this)()
                    if(this.posts.length >= this.allPosts.length) {
                        observer.unobserve(entry.target)
                    }
                }
            })
        }.bind(this))

        let target = document.querySelector('.end-of-blog')
        observer.observe(target)
    },
    methods: {
        postPath,
        getPosts() {
            getEntries()
            .then(posts => {
                this.includes = posts.includes || {}

                // Decorate everything renderable before splitting, so the
                // suppressed posts are still usable for the sidebar below.
                // Filtering them out here (as the listing used to) would
                // leave nothing to link them from.
                const renderable = (posts.items || [])
                    .filter(this.isRenderable)
                    .map(this.decorate)
                    .sort((a, b) => b.fields.date - a.fields.date)

                const featured = renderable.filter(this.isFeatured)
                const suppressed = renderable.filter(post => !this.isFeatured(post))

                this.allPosts = featured
                // Push the next 10 posts to the posts array
                this.posts = this.allPosts.slice(0, 10)

                // A suppressed post reachable only by direct URL is an
                // orphan, and an orphan gets crawled far less eagerly than
                // a linked page. The rail is what gives it an inbound link.
                // Falls back to the featured posts so a blog with nothing
                // suppressed still shows something to read next.
                this.sidebarPosts = pickRotating(suppressed.length ? suppressed : featured, 8)
            })
            .catch(error => {
                console.error('Could not load blog posts', error)
                this.allPosts = []
                this.posts = []
                this.sidebarPosts = []
            })
        },
        // featured was added to the content model after the older posts were
        // written, so only an explicit false suppresses a post from the
        // listing — an absent flag still shows. Suppressed posts stay
        // reachable at /post/:id, they just aren't promoted here.
        isFeatured(post) {
            return Boolean(post && post.fields && post.fields.featured !== false)
        },
        // A post needs a title and a body to be worth a card. Everything else
        // (hero, author, avatar, date, read time) has a fallback below, so a
        // half-filled draft costs us one card rather than the whole listing.
        isRenderable(post) {
            const fields = post && post.fields
            return Boolean(fields && fields.title && fields.content)
        },
        decorate(post) {
            const author = this.getEntry(post.fields.author)

            post.fields.image = this.getAsset(post.fields.hero)
            post.fields.authorImage = this.getAsset(author && author.profilePicture)
            post.fields.author = (author && author.name) || 'The Gamebot Team'
            post.fields.date = new Date(post.fields.date || post.sys.createdAt)
            post.preview = this.getPreview(post.fields.content)
            return post
        },
        getPreview(content) {
            const blocks = (content && content.content) || []
            const text = blocks
                .filter(block => block.nodeType === 'paragraph')
                .map(block => (block.content || []).map(node => node.value || '').join(''))
                .join(' ')
                .trim()
            return text.length > 200 ? text.substring(0, 200) + '...' : text
        },
        updatePosts() {
            return this.posts = this.allPosts.slice(0, this.posts.length + 10)
        },
        getEntry(link) {
            const entries = this.includes.Entry || []
            const entry = link && entries.find(entry => entry.sys.id === link.sys.id)
            return entry ? entry.fields : null
        },
        getAsset(link) {
            // hero and profilePicture are no longer required in Contentful, so
            // the link — or the asset it points at — can be missing entirely
            const assets = this.includes.Asset || []
            const asset = link && assets.find(asset => asset.sys.id === link.sys.id)
            const file = asset && asset.fields && asset.fields.file
            return (file && file.url) || FALLBACK_IMAGE
        },
        setSize(url, w='', h='') {
            if(url === FALLBACK_IMAGE) return url
            if(w) w = '&w=' + w
            if(h) h = '&h=' + h
            return url + '?fm=jpg' + w + h
        },
        route(post) {
            this.$router.push(`${postPath(post)}#`)
        }
    },
    computed: {
    }
}
</script>